import React, { useRef, useState } from "react";
import numbro from "numbro";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faTimesCircle,
  faEye,
  faSyncAlt,
  faUsers,
  faUsersSlash
} from "@fortawesome/free-solid-svg-icons";
import HotTable from "@handsontable/react";
import { propTypes } from "./propTypes";
import { tableSettings } from "./tableSettings";
import languages from "numbro/dist/languages.min.js";

numbro.registerLanguage(languages["de-DE"]); // €
numbro.registerLanguage(languages["en-GB"]); // £
numbro.registerLanguage(languages["ja-JP"]); // ¥

import NumberDisplay from "../../numberDisplay/numberDisplay";
import { xlsxExport } from "../../../utils/xlsxExport";
import { isFloat } from "../../../utils/isFloat";

import "./handsonTable.scss";

const getFilteredData = (filteredData, hiddenCols) =>
  filteredData.filter((_, i) => !hiddenCols.includes(i));

const CURRENCY_KEYWORDS = ["revenue", "sales", "amount", "discount"];
const CURRENCY_TITLES = ["membership", "non membership", "merchandise", "gift cards"];

const isCurrencyHeader = (title) =>
  CURRENCY_KEYWORDS.some((keyword) => title.includes(keyword)) ||
  CURRENCY_TITLES.includes(title);

const HandsonTable = (props) => {
  const [hideScroll, setHideScroll] = useState(false);
  const hotTableComponent = useRef(null);

  const adjustedDataWithoutHiddenValues = () => {
    const hi = hotTableComponent.current.hotInstance;
    let hiddenCols = [];
    let data = hi.getData();
    let headers = hi.getColHeader();
    let columns = props.columns;

    try {
      hiddenCols = hi.getPlugin("hiddenColumns").getHiddenColumns();
      // if there are hidden cols, remove them from data and headers
      if (hiddenCols.length > 0) {
        data = data.map((row) => [...getFilteredData(row, hiddenCols)]);
        headers = getFilteredData(headers, hiddenCols);
        columns = getFilteredData(props.columns, hiddenCols);
      }
    } catch (e) {
      console.error(`Exporting without hidden columns!`, e);
    }

    return { data, headers, columns };
  };

  const downloadFile = (fileType) => {
    let { data, headers, columns } = adjustedDataWithoutHiddenValues();

    xlsxExport({
      data,
      headers,
      columns: columns,
      fileName: "ikizmet_raw_data",
      fileType
    });
  };

  const resizeFirstCol = () => {
    let width = props.firstColWidth;
    if (width !== undefined) {
      let autoColumnSize = hotTableComponent.current.hotInstance.getPlugin("autoColumnSize");
      hotTableComponent.current.hotInstance.updateSettings({
        colWidths: (index) => {
          if (index === 0) {
            return width;
          } else {
            // Calculate for column at index `index` and row at index 0
            autoColumnSize.calculateColumnsWidth(index, 0, true);
            return autoColumnSize.getColumnWidth(index);
          }
        }
      });
    }
  };

  const collapseAllNestedRows = () => {
    if (props.collapseAllNestedRows) {
      const tableWrapper = document.querySelector(".wtHolder:first-of-type");
      const needToHideScroll = tableWrapper.clientWidth === tableWrapper.scrollWidth;

      if (needToHideScroll) {
        setHideScroll(true);
      }
      let nestedRows = hotTableComponent.current.hotInstance.getPlugin("nestedRows");
      nestedRows.collapsingUI.collapseAll();
    }
  };

  const reloadTableData = () => {
    props.startPolling();
  };

  let hotSettings = tableSettings(props, {
    afterInit: () => {
      setTimeout(() => {
        resizeFirstCol();
        collapseAllNestedRows();
      }, 0);
    },
    columns: props.columns
  });

  return (
    <>
      {(props.showCloseWindowButton ||
        props.showExportFileButton ||
        props.showViewAllDataButton) && (
        <span className="handsontable-buttons">
          {props.showCloseWindowButton && (
            <a onClick={() => window.close()} className="btn-raw-data" href="#">
              <FontAwesomeIcon
                className="minus-icon"
                icon={faTimesCircle}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Close Raw Data View
            </a>
          )}

          {props.showSharedMembersDataButton && (
            <a className="btn-raw-data" href={props.showSharedMembersUrl}>
              <FontAwesomeIcon
                className="minus-icon"
                icon={faUsers}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Shared
            </a>
          )}

          {props.hideSharedMembersDataButton && (
            <a className="btn-raw-data" href={props.hideSharedMembersUrl}>
              <FontAwesomeIcon
                className="minus-icon"
                icon={faUsersSlash}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Hide Shared
            </a>
          )}

          {props.showExportFileButton && props.data && props.data.length <= 999999 && (
            <a className="btn-raw-data" onClick={() => downloadFile("xlsx")}>
              <FontAwesomeIcon
                className="minus-icon"
                icon={faDownload}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Download as Excel
            </a>
          )}

          {props.showExportFileButton && props.showExportCSV && (
            <a className="btn-raw-data" onClick={() => downloadFile("csv")}>
              <FontAwesomeIcon
                className="minus-icon"
                icon={faDownload}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Download as CSV
            </a>
          )}

          {props.showViewAllDataButton && (
            <a className="btn-raw-data" href={props.showViewAllDataUrl}>
              <FontAwesomeIcon
                className="minus-icon"
                icon={faEye}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Show All Data
            </a>
          )}

          {process.env.NODE_ENV !== "production" && props.startPolling !== undefined && (
            <a className="btn-raw-data" onClick={reloadTableData}>
              <FontAwesomeIcon
                className="sync-alt"
                icon={faSyncAlt}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Refresh Data
            </a>
          )}

          {props.showViewSummaryButton && (
            <a className="btn-raw-data" href={props.showViewSummaryUrl}>
              <FontAwesomeIcon
                className="minus-icon"
                icon={faEye}
                style={{ color: "var(--color-icons-bg)" }}
              />{" "}
              Show Summary Data
            </a>
          )}

          {props.showViewBackButton && (
            <a className="btn-raw-data" href={props.showViewBackUrl}>
              <FontAwesomeIcon
                className="minus-icon"
                icon={faEye}
                style={{ color: "var(--color-icons-bg)" }}
              />
              Back to Detail View
            </a>
          )}

          {props.labelInfo && <h2 className="handsontable-label">{props.labelInfo}</h2>}
        </span>
      )}

      {props.headerInfo && Array.isArray(props.headerInfo) && (
        <div className="handsontable-actions">
          {props.headerInfo.map((data, i) => {
            let title = data[0].toLowerCase().trim();
            const isFloatValue = isFloat(data[1]);
            const value = isFloatValue ? data[1].toFixed(1) : data[1];
            const isCurrency = isCurrencyHeader(title);

            return (
              <div key={i} className="handsontable-actions__wrapper">
                <div className="title">{data[0]}</div>
                <div className="text">
                  {isCurrency ||
                  isFloatValue ? (
                    value
                  ) : (
                    <NumberDisplay value={value} size="24px" disableAnimation />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <div className={`hot__container ${hideScroll ? "hide-scroll" : ""}`}>
        <HotTable ref={hotTableComponent} settings={hotSettings} />
      </div>
    </>
  );
};

HandsonTable.propTypes = propTypes;

export default HandsonTable;
